// src/components/Header/index.tsx
import { useState } from "react";
import { Row, Drawer } from "antd";
import { withTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Container from "../../common/Container";
import { SvgIcon } from "../../common/SvgIcon";
import {
  HeaderSection,
  LogoContainer,
  Burger,
  NotHidden,
  CustomNavLinkSmall,
  Span,
  LaunchButton,
  DropdownContainer,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "./styles";

const Header = ({ t, i18n }: { t: Function; i18n: any }) => {
  const [visible, setVisibility] = useState(false);
  const location = useLocation();

  const toggleDrawer = () => {
    setVisibility(!visible);
  };

  // Function to generate language-aware URLs
  const getLangUrl = (path: string): string => {
    const currentLanguage = i18n.language;
    
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    
    const isLanguagePrefix = ['en', 'es', 'fr', 'de', 'it', 'da', 'sv', 'no', 'nl'].includes(firstSegment);
    
    const languageToUse = isLanguagePrefix ? firstSegment : currentLanguage;
    
    if (languageToUse === 'en') {
      return path;
    }
    
    return `/${languageToUse}${path}`;
  };

  const MenuItem = () => {
    return (
      <>
        <CustomNavLinkSmall>
          <a href={getLangUrl("/pricing")}>
            <Span>{t("Pricing")}</Span>
          </a>
        </CustomNavLinkSmall>
        <CustomNavLinkSmall>
          <a href={getLangUrl("/security")}>
            <Span>{t("Security")}</Span>
          </a>
        </CustomNavLinkSmall>
        <CustomNavLinkSmall>
          <DropdownContainer>
            <DropdownTrigger>
              <Span>{t("About")}</Span>
              <svg 
                width="12" 
                height="8" 
                viewBox="0 0 12 8" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginLeft: '4px', transition: 'transform 0.2s ease' }}
              >
                <path 
                  d="M1 1.5L6 6.5L11 1.5" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </DropdownTrigger>
            <DropdownMenu>
              <DropdownItem>
                <a href={getLangUrl("/terms")}>
                  {t("Terms of Service")}
                </a>
              </DropdownItem>
              <DropdownItem>
                <a href={getLangUrl("/privacy")}>
                  {t("Privacy Statement")}
                </a>
              </DropdownItem>
            </DropdownMenu>
          </DropdownContainer>
        </CustomNavLinkSmall>
        <CustomNavLinkSmall>
          <LaunchButton href="http://app.totext.eu">
            {t("Launch app")}
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-embed-xxsmall">
              <path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M12.9697 3.96967C13.2626 3.67678 13.7374 3.67678 14.0303 3.96967L21.5303 11.4697C21.671 11.6103 21.75 11.8011 21.75 12C21.75 12.1989 21.671 12.3897 21.5303 12.5303L14.0303 20.0303C13.7374 20.3232 13.2626 20.3232 12.9697 20.0303C12.6768 19.7374 12.6768 19.2626 12.9697 18.9697L19.1893 12.75H3C2.58579 12.75 2.25 12.4142 2.25 12C2.25 11.5858 2.58579 11.25 3 11.25H19.1893L12.9697 5.03033C12.6768 4.73744 12.6768 4.26256 12.9697 3.96967Z" 
                fill="currentColor"
              ></path>
            </svg>
          </LaunchButton>
        </CustomNavLinkSmall>
      </>
    );
  };

  const MobileMenuItem = () => {
    return (
      <>
        <CustomNavLinkSmall>
          <a href={getLangUrl("/pricing")}>
            <Span>{t("Pricing")}</Span>
          </a>
        </CustomNavLinkSmall>
        <CustomNavLinkSmall>
          <a href={getLangUrl("/security")}>
            <Span>{t("Security")}</Span>
          </a>
        </CustomNavLinkSmall>
        {/* Mobile: Show legal links directly instead of dropdown */}
        <CustomNavLinkSmall>
          <a href={getLangUrl("/terms")}>
            <Span>{t("Terms of Service")}</Span>
          </a>
        </CustomNavLinkSmall>
        <CustomNavLinkSmall>
          <a href={getLangUrl("/privacy")}>
            <Span>{t("Privacy Statement")}</Span>
          </a>
        </CustomNavLinkSmall>
        <CustomNavLinkSmall>
          <LaunchButton href="http://app.totext.eu">
            {t("Launch app")}
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-embed-xxsmall">
              <path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M12.9697 3.96967C13.2626 3.67678 13.7374 3.67678 14.0303 3.96967L21.5303 11.4697C21.671 11.6103 21.75 11.8011 21.75 12C21.75 12.1989 21.671 12.3897 21.5303 12.5303L14.0303 20.0303C13.7374 20.3232 13.2626 20.3232 12.9697 20.0303C12.6768 19.7374 12.6768 19.2626 12.9697 18.9697L19.1893 12.75H3C2.58579 12.75 2.25 12.4142 2.25 12C2.25 11.5858 2.58579 11.25 3 11.25H19.1893L12.9697 5.03033C12.6768 4.73744 12.6768 4.26256 12.9697 3.96967Z" 
                fill="currentColor"
              ></path>
            </svg>
          </LaunchButton>
        </CustomNavLinkSmall>
      </>
    );
  };

  return (
    <HeaderSection>
      <Container>
        <div style={{ overflow: 'visible', position: 'relative' }}>
        <Row justify="space-between">
          <LogoContainer to={getLangUrl("/")} aria-label="homepage">
            <SvgIcon src="logo.png" height="40px" width="Auto" />
          </LogoContainer>
          <NotHidden>
            <MenuItem />
          </NotHidden>
          <Burger onClick={toggleDrawer}>
            <div className="menu-button-wrapper">
              <div className="menu-button-horziontal">
                <div className="menu-button-horizontal-line line-1"></div>
                <div className="menu-button-horizontal-line line-2"></div>
                <div className="menu-button-horizontal-line line-3"></div>
              </div>
            </div>
          </Burger>
          <Drawer 
            placement="right"
            closable={false}
            onClose={toggleDrawer}
            visible={visible}
            width={300}
          >
            <div style={{ marginTop: "60px" }}>
              <MobileMenuItem />
            </div>
          </Drawer>
        </Row>
        </div>
      </Container>
    </HeaderSection>
  );
};

export default withTranslation()(Header);